import React from 'react';
import toast, { Toaster } from 'react-hot-toast';

const normalizeToastMessage = (message: unknown, fallback = 'Something went wrong'): string => {
  if (typeof message === 'string' && message.trim()) return message;
  if (message && typeof message === 'object' && 'message' in message) {
    const nested = (message as { message?: unknown }).message;
    if (typeof nested === 'string' && nested.trim()) return nested;
  }
  return fallback;
};

/** Suppress identical toasts (type + message) for this window — API interceptor + UI often both fire. */
const DEDUPE_MS = 2500;
const recentToasts = new Map<string, number>();

function shouldSkipDuplicate(id: string): boolean {
  const now = Date.now();
  const last = recentToasts.get(id) || 0;
  if (now - last < DEDUPE_MS) return true;
  recentToasts.set(id, now);
  // Bound map size
  if (recentToasts.size > 80) {
    const cutoff = now - DEDUPE_MS * 2;
    Array.from(recentToasts.entries()).forEach(([key, ts]) => {
      if (ts < cutoff) recentToasts.delete(key);
    });
  }
  return false;
}

/**
 * Site-wide toast helper.
 * Same type+message within ~2.5s only shows once (also uses stable hot-toast `id`).
 */
export const showToast = (
  type: 'success' | 'error' | 'info',
  message: string,
  duration: number = 3000
) => {
  const safeMessage = normalizeToastMessage(message);
  const id = `${type}:${safeMessage}`;
  if (shouldSkipDuplicate(id)) return;

  switch (type) {
    case 'success':
      toast.success(safeMessage, {
        id,
        duration,
        position: 'top-center',
        style: {
          borderRadius: '12px',
          padding: '16px',
          background: '#fff',
          color: '#1f2937',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          border: '1px solid #10b981',
          fontSize: '14px',
          fontWeight: '500',
        },
        iconTheme: {
          primary: '#10b981',
          secondary: '#fff',
        },
      });
      break;
    case 'error':
      toast.error(safeMessage, {
        id,
        duration: 5000,
        position: 'top-right',
        style: {
          borderRadius: '12px',
          padding: '16px',
          background: '#fff',
          color: '#1f2937',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          border: '1px solid #ef4444',
          fontSize: '14px',
          fontWeight: '500',
        },
        iconTheme: {
          primary: '#ef4444',
          secondary: '#fff',
        },
      });
      break;
    case 'info':
      toast(safeMessage, {
        id,
        duration,
        position: 'top-right',
        icon: 'ℹ️',
        style: {
          borderRadius: '12px',
          padding: '16px',
          background: '#fff',
          color: '#1f2937',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          border: '1px solid #f59e0b',
          fontSize: '14px',
          fontWeight: '500',
        },
        iconTheme: {
          primary: '#f59e0b',
          secondary: '#fff',
        },
      });
      break;
  }
};

export const ToastProvider: React.FC = () => {
  return (
    <Toaster
      position="top-right"
      reverseOrder={false}
      gutter={8}
      toastOptions={{
        duration: 4000,
        style: {
          background: '#fff',
          color: '#1f2937',
          borderRadius: '12px',
          padding: '16px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          fontSize: '14px',
          fontWeight: '500',
          maxWidth: '400px',
        },
        success: {
          duration: 4000,
          iconTheme: {
            primary: '#10b981',
            secondary: '#fff',
          },
          style: {
            borderColor: '#10b981',
            background: '#fff',
            color: '#1f2937',
          },
        },
        error: {
          duration: 5000,
          iconTheme: {
            primary: '#ef4444',
            secondary: '#fff',
          },
          style: {
            borderColor: '#ef4444',
            background: '#fff',
            color: '#1f2937',
          },
        },
        loading: {
          iconTheme: {
            primary: '#f59e0b',
            secondary: '#fff',
          },
          style: {
            borderColor: '#f59e0b',
            background: '#fff',
            color: '#1f2937',
          },
        },
      }}
    />
  );
};

export default showToast;
